<template>
  <div class="tree-structure">
    <!--  Node name toggle -->
    <div v-if="showNodeNameToggle" class="lo-wrapper">
      <cv-toggle
        class="toggle"
        value="view-display-learning-outcome-toggle"
        :label="nodeNameLabel"
        :checked="showNodeName"
        small
        @change="toggleNodeName"
      >
        <template #text-left>{{ nodeNameLabel }}</template>
        <template #text-right>{{ nodeNameLabel }}</template>
      </cv-toggle>
      <!--   Node name tooltip  -->
      <cv-tooltip
        direction="bottom"
        :alignment="tooltipAlignment"
        :tip="nodeNameTooltip"
        class="info-tooltip"
      >
        <svg-icon name="Info24" kind="action" />
      </cv-tooltip>
    </div>
    <Tree
      v-model:nodes="nodes"
      :use-checkbox="useCheckBox"
      :use-icon="true"
      @node-click="onNodeClick"
    >
      <template #iconActive>
        <svg-icon :name="activeIconName" :kind="iconKindValue" />
      </template>
      <template #iconInactive>
        <svg-icon :name="inactiveIconName" :kind="iconKindValue" />
      </template>
      <template #checkbox="props">
        <div
          class="node-container"
          :class="{ 'learning-outcome-node-container': props.node.data?.isLearningOutcome }"
          @click="onNodeContainerClick($event, props)"
        >
          <input-checkbox
            v-if="useCheckBox && isNodeSelectable(props.node)"
            :id="checkBoxId(props)"
            :kind="inputKindValue"
            value="true"
            :selected-values="[String(props.checked)]"
            :mixed="props.indeterminate"
            :disabled="isInputDisabled(props.node)"
          />
          <input-radio
            v-if="useRadio && isNodeSelectable(props.node)"
            :id="checkBoxId(props)"
            :kind="inputKindValue"
            value="true"
            :selected-value="String(selectedRadioNodeId === props.node.id)"
          />
          <svg-icon
            v-if="props.node.data && props.node.data.isLearningOutcome"
            :name="props.node.data.iconName"
            :class="props.node.data.cssClasses"
            kind="indicative"
          />
          <div v-if="isPlainTextNode" class="plain-text">
            <div class="node-text">{{ getNodeText(props.node) }}</div>
            <div v-if="showNodeName" class="node-display-name">{{ getNodeName(props.node) }}</div>
            <div v-if="showCheckedCountForParentNode(props.node)" class="checked-node-counter">
              <span>
                {{ checkedCount(props.node) }}
              </span>
              <svg-icon name="ForwardSlash16" kind="indicative" />
              <span class="max-count">{{ maxCheckedCount }}</span>
            </div>
          </div>
          <template v-else>
            <wiris-rich-text :content="getNodeText(props.node)" />
            <div v-if="showNodeName" class="node-display-name">
              {{ getNodeName(props.node) }}
            </div>
          </template>
        </div>
        <div v-if="hasError(props.node)" class="tree-row-item-error">
          <div class="icon-wrapper">
            <svg-icon name="Error16" kind="indicative" />
          </div>
          <span> {{ errorMessage(props.node) }} </span>
        </div>
      </template>
    </Tree>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Tree from "vue3-tree";
import DeviceUtils from "@utils/device.utils";
import ObjectUtils from "@utils/object.utils";
import { isEmpty } from "@utils/var.check";
import { isNull } from "@utils/var.check";

interface PropertyNames {
  text?: string;
}

interface TreeNode {
  id: number;
  checked?: boolean;
  nodes?: TreeNode[];
  data?: Record<string, unknown>;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  name?: string;
  parent?: TreeNode | null;
  [key: string]: unknown;
}

const props = defineProps<{
  data: TreeNode[] | null;
  isPlainTextNode: boolean;
  propertyNames: PropertyNames | null;
  reload: string;
  useCheckBox: boolean;
  useRadio: boolean;
  selectedRadioNodeId?: number | null;
  editable: boolean;
  onlyLeafSelectable: boolean;
  showCheckedCount: boolean;
  maxCheckedCount: number;
  showNodeName: boolean;
  showNodeNameToggle: boolean;
  nodeNameLabel: string;
  nodeNameTooltip: string;
  iconActive?: string;
  iconInactive?: string;
  iconKind?: string;
  inputKind?: string;
}>();

const emit = defineEmits<{
  (e: "node_checked", node: TreeNode): void;
  (e: "node_unchecked", node: TreeNode): void;
  (e: "nodeNameToggled", value: boolean): void;
}>();

const nodes = ref<TreeNode[]>(ObjectUtils.deepClone(props.data ?? []));
const selectedRadioNodeId = ref<number | null>(props.selectedRadioNodeId ?? null);

const checkBoxId = (props: { node: TreeNode }): string => `tree_node_${props.node.id}`;

const isInputDisabled = (node: TreeNode): boolean => !!node.disabled;

const hasError = (node: TreeNode): boolean => !!node.error;

const errorMessage = (node: TreeNode): string => node.errorMessage ?? "";

const tooltipAlignment = computed(() => (DeviceUtils.isMobile() ? "end" : "center"));

const activeIconName = computed(() => props.iconActive ?? "ArrowDown24");
const inactiveIconName = computed(() => props.iconInactive ?? "ArrowUp24");
const iconKindValue = computed(() => props.iconKind ?? "action");
const inputKindValue = computed(() => props.inputKind ?? "white");

watch(
  () => props.reload,
  () => {
    nodes.value = props.data ?? [];
  },
);

watch(
  () => props.data,
  (newVal) => {
    nodes.value = ObjectUtils.deepClone(newVal ?? []);
  },
  { deep: true },
);

watch(
  () => props.useRadio,
  () => {
    uncheckAllNode();
  },
);

watch(
  () => props.selectedRadioNodeId,
  (newValue) => {
    selectedRadioNodeId.value = newValue ?? null;
  },
);

const toggleNodeName = (value: boolean): void => {
  emit("nodeNameToggled", value);
};

const getNodeText = (node: TreeNode): unknown => {
  if (props.propertyNames?.text) {
    return node[props.propertyNames.text];
  }
  return node.text;
};

const getNodeName = (node: TreeNode): unknown => node.name;

const isNodeSelectable = (node: TreeNode): boolean => {
  if (props.onlyLeafSelectable && node.nodes && node.nodes.length !== 0) {
    return false;
  }
  return true;
};

const showCheckedCountForParentNode = (node: TreeNode): boolean => {
  return !!(props.showCheckedCount && node.nodes && node.nodes.length !== 0);
};

const onCheckboxClick = (
  event: Event,
  props: { node: TreeNode; toggleCheckbox: () => void; checked: boolean },
): void => {
  if (props.node.disabled) {
    return;
  }

  event.stopPropagation();
  event.preventDefault();
  props.toggleCheckbox();

  if (!props.checked) {
    emit("node_checked", props.node);
  } else {
    emit("node_unchecked", props.node);
  }
};

const onNodeClick = (node: TreeNode): void => {
  console.log("onClick", node);
};

const getCheckedLeafNodes = (): TreeNode[] => {
  const getCheckedNodes = (
    nodesArr: TreeNode[],
    parent: TreeNode | null,
    result: TreeNode[] = [],
  ): TreeNode[] => {
    for (const node of nodesArr) {
      if (!node.nodes || node.nodes.length === 0) {
        if (node.checked) {
          node.parent = parent;
          result.push(node);
        }
      } else {
        getCheckedNodes(node.nodes, node, result);
      }
    }
    return result;
  };
  return getCheckedNodes(nodes.value, null);
};

const getUncheckedLeafNodes = (): TreeNode[] => {
  const getUncheckedNodes = (
    nodesArr: TreeNode[],
    parent: TreeNode | null,
    result: TreeNode[] = [],
  ): TreeNode[] => {
    for (const node of nodesArr) {
      if (!node.nodes || node.nodes.length === 0) {
        if (!node.checked) {
          node.parent = parent;
          result.push(node);
        }
      } else {
        getUncheckedNodes(node.nodes, node, result);
      }
    }
    return result;
  };
  return getUncheckedNodes(nodes.value, null);
};

const checkedCount = (node: TreeNode): number => {
  if (!node.nodes) return 0;
  return node.nodes.filter((childNode) => childNode.checked).length;
};

const onRadioClick = (event: Event, props: { node: TreeNode }): void => {
  event.stopPropagation();
  event.preventDefault();
  selectedRadioNodeId.value = props.node.id;
  resetNodeCheckedState(nodes.value, props.node.id);
  emit("node_checked", props.node);
};

const resetNodeCheckedState = (nodesArr: TreeNode[], selectedId: number | null): void => {
  nodesArr.forEach((node) => {
    if (!isNull(selectedId) && node.id === selectedId) {
      node.checked = true;
    } else {
      node.checked = false;
    }
    if (node.nodes && node.nodes.length > 0) {
      resetNodeCheckedState(node.nodes, selectedId);
    }
  });
};

const uncheckAllNode = (): void => {
  selectedRadioNodeId.value = null;
  resetNodeCheckedState(nodes.value, null);
  nodes.value.forEach((node) => {
    emit("node_checked", node);
  });
};

const showErrorMessage = (nodeIds: number[], errorMsg: string): void => {
  const setError = (node: TreeNode): void => {
    if (isEmpty(nodeIds)) {
      return;
    }
    if (nodeIds.includes(node.id)) {
      node.error = true;
      node.errorMessage = errorMsg;

      const index = nodeIds.indexOf(node.id);
      if (index > -1) {
        nodeIds.splice(index, 1);
      }
    }
    if (isEmpty(node.nodes) || isEmpty(nodeIds)) {
      return;
    } else if (Array.isArray(node.nodes)) {
      node.nodes.forEach(setError);
    }
  };
  nodes.value.forEach(setError);
};

const clearError = (): void => {
  const clearErrorRecursive = (node: TreeNode): void => {
    node.error = false;
    node.errorMessage = "";
    if (Array.isArray(node.nodes) && node.nodes.length > 0) {
      node.nodes.forEach(clearErrorRecursive);
    }
  };
  nodes.value.forEach(clearErrorRecursive);
};

const onNodeContainerClick = (event, slotProps) => {
  if (slotProps.node.disabled) return;

  if (props.useCheckBox && isNodeSelectable(slotProps.node)) {
    onCheckboxClick(event, slotProps);
  } else if (props.useRadio && isNodeSelectable(slotProps.node)) {
    onRadioClick(event, slotProps);
  }
};

defineExpose({
  getCheckedLeafNodes,
  getUncheckedLeafNodes,
  showErrorMessage,
  clearError,
});
</script>

<style lang="scss">
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/layout/breakpoint";

.tree-structure {
  .tree {
    > .tree-list {
      // First layer
      > .tree-row {
        padding: $spacing-02 $spacing-03;
        padding-left: $spacing-05 !important;

        &::before,
        &::after {
          display: none;
        }

        + .tree-row {
          margin-top: $spacing-05;
        }
      }
    }
  }
  .tree-list {
    .tree-list {
      .tree-row-item-icon-wrapper {
        right: 0.25rem;
      }
    }
  }
  .tree-row {
    position: relative;
    background-color: $ui-background;
    border-radius: 10px;

    &::before {
      content: "";
      position: absolute;
      width: 0.75rem;
      height: 1rem;
      top: 0;
      margin-left: -1rem;
      border-bottom: 1px solid $ui-05;
    }

    &::after {
      content: "";
      position: absolute;
      height: 100%;
      top: 0;
      margin-left: -1rem;
      border-left: 1px solid $ui-05;
      z-index: z-map("default");
    }

    &:last-child {
      &::after {
        height: 1rem;
      }
    }

    .plain-text {
      display: flex;
      align-items: center;
      flex: 1 1 auto;
    }

    .rich-text {
      // Handle long Wiris images added in content map by displaying the horizontal scrollbar
      overflow-x: auto;
      line-height: 1.5rem;
      border-radius: 0;

      .rich-text-content {
        @include carbon--type-style("body-short-01", true);
        line-height: normal;
      }
    }
  }
  .tree-row-item {
    display: flex;
    padding-right: $spacing-07;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    cursor: pointer;

    &:has(.tree-row-item-error) {
      flex-direction: column;
      align-items: flex-start;

      .tree-row-item-icon-wrapper {
        top: 9px; // to align arrow to center when error message is present
      }

      .input-checkbox {
        .sls-icon[name="CheckBoxChecked24"] {
          [stroke] {
            stroke: $support-01;
          }
        }
      }
    }

    &:has(.input-checkbox.disabled) {
      cursor: not-allowed;

      .tree-row-item-icon-wrapper {
        cursor: pointer;
      }
    }

    .node-container {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: $spacing-02 0 $spacing-02 0;

      .node-display-name {
        @include carbon--type-style("label-01", true);
        display: block;
        color: $text-02;
      }
    }

    .learning-outcome-node-container {
      display: grid;
      grid-template-columns: auto auto 1fr;
      grid-template-rows: auto auto;

      .input-checkbox {
        grid-row: 1;
        grid-column: 1;
      }

      > .sls-icon {
        grid-row: 1;
        grid-column: 2;
      }

      .rich-text {
        grid-row: 1;
        grid-column: 3;
      }

      .node-display-name {
        grid-row: 2;
        grid-column: 3;
      }
    }

    .tree-row-item-error {
      @include carbon--type-style("helper-text-01", true);
      display: flex;
      align-items: center;
      gap: $spacing-02;
      margin-bottom: $spacing-03;
      color: $text-error;

      .icon-wrapper {
        display: inline-flex;
      }
    }
  }

  .tree-row-item-icon-wrapper {
    display: inline-flex;
    position: absolute;
    right: 0.75rem;

    .sls-icon {
      [stroke] {
        stroke: $interactive-01;
      }

      [fill] {
        fill: $interactive-01;
      }
    }
  }

  .input-checkbox {
    margin: 0;
    align-self: flex-start;
    flex: 0 0 auto;
    overflow: hidden; // Fix unintended scrollbar

    & + .sls-icon {
      align-self: flex-start;
      margin-right: $spacing-03;
    }

    &.disabled {
      & + .sls-icon {
        opacity: 0.5;
      }
      & ~ .rich-text {
        color: $disabled-02;
      }
    }

    & ~ .rich-text {
      overflow: unset;
    }
  }

  .cv-tooltip {
    margin-left: $spacing-02;
  }

  .lo-wrapper {
    display: flex;
    align-items: center;
    padding: $spacing-03 0;
    gap: $spacing-02;
    .sls-icon {
      min-width: 24px;
    }

    .bx--toggle-input__label {
      @include carbon--type-style("body-short-01");
      display: flex;
      flex-flow: row-reverse;
      align-items: center;
      gap: $spacing-03;
      color: $text-02;
    }

    .toggle {
      max-width: fit-content;

      &.bx--toggle-input--small {
        & + .bx--toggle-input__label {
          .bx--toggle__switch {
            margin-top: 0;
            flex: none;
          }
        }
      }

      .bx--toggle__text--off,
      .bx--toggle__text--on {
        display: none;
      }
    }

    .info-tooltip {
      color: $icon-01;

      .sls-icon {
        [stroke] {
          stroke: currentColor;
        }

        [fill] {
          fill: currentColor;
        }
      }
    }
  }

  .input-radio {
    align-self: flex-start;
  }

  .node-text {
    line-height: 1.125rem;
  }

  .checked-node-counter {
    display: flex;
    align-items: center;
    margin-left: auto;
    > span {
      @include carbon--type-style("button-02");
      &:first-of-type {
        padding: 0 $spacing-03;
        background-color: $focus;
        color: $interactive-01;
        border-radius: 5px;
      }
      &:last-of-type {
        color: $text-02;
      }
    }
  }
}
</style>
